// 블록 ④ 채널 브랜디드 퍼포먼스 — 채널 × 포맷 (평균 조회수 + 구독자 대비 시청률).
//
// 포맷 칸은 **채널마다 다르다**(../platformFormat): YouTube 롱/숏 · Instagram 게시물/릴스 ·
// TikTok 영상 한 칸. 이 블록은 세 채널을 한 표에 세로로 쌓으므로, 칸 수와 어휘가 줄마다 갈린다.
// 전부 롱/숏으로 그리면 TikTok 줄이 "LONG — · 집계 콘텐츠 없음"처럼 **없는 포맷의 결측**을
// 주장하게 된다(실제로 그렇게 나와서 잡혔다).
//
// long=핑크 / short=블루 색코딩은 목업(디자이너 라운드 확정) 그대로 — 슬롯 기준이라 어휘가
// 바뀌어도 색은 유지된다. 헤더 색 범례는 두지 않는다: 줄마다 어휘가 달라 하나로 못 적고,
// 칸 라벨이 이미 같은 색으로 값 바로 위에 붙어 있어 범례 없이도 읽힌다.
//
// watchRate는 구독자 대비 → 숏폼 >1 정상(상한 없음, 10배 이상은 ×N 표기). 분모0 가드는 formatRate에서 "—".
// 빈 배열이면 블록 숨김(graceful) — 부모 grid가 매출 블록을 전체폭으로 흡수.
//
// 표기 밀도(§5.0): 결측 사유 캡션은 셀마다 붙이지 않고 **블록 각주 1회**로 집약한다.
//   예외는 저신뢰(값이 있는데 못 믿는 경우) — 각주로 밀면 값과 분리돼 오독된다.
import type { ChannelPerformance as ChannelPerf, ContentFormat, DataMeta, Platform } from '../../creatorsApi'
import { AiJudgedNotice } from '../AiJudgedNotice'
import { MetricCaption, isMissing, resolveMetric, type MetricStatus } from '../metric'
import { formatSlots, missingCaption, sampleText } from '../platformFormat'
import { PLATFORM_LABEL, basisFragments, formatCount, formatRate } from './format'

export function ChannelPerformance({
  channels,
  meta,
}: {
  channels: ChannelPerf[]
  meta?: DataMeta
}) {
  if (channels.length === 0) return null

  // 채널별 상태를 먼저 판정한다 — 각주(블록 1회 집약)와 셀 캡션이 같은 판정을 공유해야 어긋나지 않는다.
  const rows = channels.map((c) => {
    const status: Record<ContentFormat, MetricStatus> = {
      long: resolveMetric({
        value: c.avgViews.long,
        sampleSize: c.sampleSize?.long,
        reason: c.reason?.long,
      }),
      short: resolveMetric({
        value: c.avgViews.short,
        sampleSize: c.sampleSize?.short,
        reason: c.reason?.short,
      }),
    }
    // 판정은 **그리는 칸만** 본다. TikTok 의 long 슬롯은 화면에 없으므로 결측 여부도 무의미하다.
    const drawn = formatSlots(c.platform).map((f) => status[f.slot])
    return {
      c,
      status,
      // 채널 전체가 결측이면 셀 캡션을 반복하지 않고 각주로 집약한다(§5.0 표기 밀도).
      allMissing: drawn.every((s) => !s.hasValue),
      // 그리는 칸이 모두 같은 사유면 각주에서 칸 이름을 생략한다("YouTube 집계 콘텐츠 없음").
      sharedCaption:
        drawn[0]?.caption && drawn.every((s) => s.caption === drawn[0].caption) ? drawn[0].caption : null,
    }
  })

  // 각주 — 전 지표가 빈 채널의 **사유**를 1회 집약 + 집계 기준. 사유를 모르면 목록에서 빠진다.
  const emptyNotes = rows
    .filter((r) => r.allMissing)
    .map((r) => {
      const caption = r.sharedCaption ?? missingCaption(r.c.platform, r.status)
      return caption ? `${PLATFORM_LABEL[r.c.platform]} ${caption}` : null
    })
    .filter((s): s is string => s != null)
  // 집계창·수집일 규칙은 공용 조각 빌더가 단일 원천이다(여기서 다시 구현하지 않는다).
  // AI 고지는 각주가 아니라 제목 아래 줄로 낸다(다른 블록과 같은 자리·같은 문구).
  const basis = basisFragments(meta)

  return (
    <div className="min-w-0 border border-line-hairline bg-paper">
      <div className="flex items-center justify-between border-b border-line-hairline p-[16px_20px]">
        <div className="flex items-center gap-2">
          <span className="inline-block h-[5px] w-[5px] bg-brand-blue" aria-hidden />
          <h3 className="font-display text-[20px] font-bold tracking-[-0.01em] text-fg-1">채널 브랜디드 퍼포먼스</h3>
        </div>
      </div>

      {/* 이 블록도 AI 고지가 필요하다 — 표에 IG·TikTok 행이 서고, 그 행의 모수가 되는
          "브랜디드인가"를 캡션 AI 가 판정한다. 조회수·시청률 자체는 플랫폼 원본이지만
          **어떤 콘텐츠를 셌는지**가 AI 산출물이라 값의 근거가 갈린다. */}
      <div className="p-[14px_20px_0]">
        <AiJudgedNotice />
      </div>

      {rows.map(({ c, status, allMissing }) => {
        const sample = sampleText(c.platform, c.sampleSize)
        return (
          <div key={c.platform} className="border-b border-line-hairline p-[15px_20px] last:border-b-0">
            <div className="mb-3 flex items-baseline justify-between gap-3">
              <span className="font-display text-[16px] font-medium text-fg-1">{PLATFORM_LABEL[c.platform]}</span>
              <span className="whitespace-nowrap font-text text-[11px] tabular-nums text-fg-3">
                구독 {formatCount(c.subscribers)}
                {sample ? ` · 표본 ${sample}` : ''}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-5">
              <MetricPair
                label="평균 조회수"
                platform={c.platform}
                values={c.avgViews}
                fmt={formatCount}
                status={status}
                suppressCaption={allMissing}
              />
              <div className="border-l border-line-hairline pl-5">
                <MetricPair
                  label="구독자 대비 시청률"
                  platform={c.platform}
                  values={c.watchRate}
                  fmt={formatRate}
                />
              </div>
            </div>
          </div>
        )
      })}

      {(emptyNotes.length > 0 || basis.length > 0) && (
        <div className="border-t border-line-hairline bg-surface-soft p-[11px_20px]">
          <p className="m-0 break-keep font-text text-[11px] leading-[1.5]">
            {emptyNotes.length > 0 && (
              <span className="font-medium text-fg-2">{emptyNotes.join(' · ')}. </span>
            )}
            {basis.length > 0 && <span className="text-fg-3">{basis.join(' · ')}</span>}
          </p>
        </div>
      )}
    </div>
  )
}

/** 한 지표의 포맷 칸들. 칸 수·라벨은 플랫폼이 정하고(FORMAT_LABELS), 색은 슬롯이 정한다. */
function MetricPair({
  label,
  platform,
  values,
  fmt,
  status,
  suppressCaption,
}: {
  label: string
  platform: Platform
  values: Record<ContentFormat, number>
  fmt: (v: number | null | undefined) => string
  /** 결측/저신뢰 판정. 시청률 칸처럼 판정이 없는 자리에선 생략한다. */
  status?: Record<ContentFormat, MetricStatus>
  suppressCaption?: boolean
}) {
  return (
    <div>
      <div className="mb-2 font-text text-[11px] tracking-[0.04em] text-fg-3">{label}</div>
      <div className="flex gap-4">
        {formatSlots(platform).map((f) => {
          const tone = f.slot === 'long' ? 'text-brand-pink' : 'text-brand-blue'
          return (
            <div key={f.slot} className="min-w-0">
              <div className={`font-text text-[11px] ${tone}`}>{f.label}</div>
              <div
                className={`whitespace-nowrap font-display text-[20px] font-bold leading-none tabular-nums ${tone}`}
              >
                {fmt(values[f.slot])}
              </div>
              <ValueCaption status={status?.[f.slot]} suppress={suppressCaption} />
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** 값 옆 캡션 — 정상 모수(①)는 채널 헤더 우측이 이미 말하므로 생략하고,
 *  저신뢰(②)와 한쪽만 결측인 경우만 값 옆에 붙인다. 채널 전체 결측이면 각주로 넘긴다. */
function ValueCaption({ status, suppress }: { status?: MetricStatus; suppress?: boolean }) {
  if (suppress || !status?.caption || status.state === 'ok') return null
  if (status.state !== 'low_sample' && !isMissing(status.state)) return null
  return (
    <div className="mt-[5px]">
      <MetricCaption status={status} />
    </div>
  )
}
